import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Checkbox, Collapse, CircularProgress, FormControlLabel, Paper, Radio, RadioGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import axiosInstance from './axiosInstance';

const filterParamValue = (value) => {
  if (Array.isArray(value)) {
    const values = value.filter((item) => item !== null && item !== undefined && String(item).trim() !== '');
    return values.length ? values.join(',') : undefined;
  }
  if (value === null || value === undefined || String(value).trim() === '') return undefined;
  return value;
};

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '£0.00';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0.00%';
  return `${number.toFixed(2)}%`;
};

const getProfitColor = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'text.primary';
  return number >= 0 ? 'success.main' : 'error.main';
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const roundMoney = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

const compareText = (left, right) => String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base', numeric: true });

const compareNumbers = (left, right) => toNumber(left) - toNumber(right);

const sortByConfig = (items, sortConfig, accessors) => {
  const rows = Array.isArray(items) ? [...items] : [];
  const sortKey = sortConfig?.key;
  const accessor = accessors?.[sortKey];
  if (!sortKey || !accessor) return rows;

  const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;
  return rows.sort((left, right) => {
    const leftValue = accessor.value(left);
    const rightValue = accessor.value(right);
    const result = accessor.type === 'number'
      ? compareNumbers(leftValue, rightValue)
      : compareText(leftValue, rightValue);
    if (result !== 0) return result * directionMultiplier;
    return compareText(left?.job_title || left?.supplier_name || left?.full_name, right?.job_title || right?.supplier_name || right?.full_name);
  });
};


const COST_CATEGORY_COLORS = {
  labour_cost: '#4778f5',
  parts_cost: '#55aa58',
  subcontracted_cost: '#ff9800',
  adas_cost: '#4fc3c7',
  consumables_cost: '#9c5be3',
  paint_cost: '#f9b436',
  fleet_cost: '#64748b',
  car_hire_cost: '#06b6d4',
  storage_recovery_cost: '#94a3b8',
};

const calculateSelectedTotalCost = (row, includedCostColumns) => {
  const optionalFleetCost = includedCostColumns.fleet_cost ? toNumber(row?.fleet_cost) : 0;
  const optionalCarHireCost = includedCostColumns.car_hire_cost ? toNumber(row?.car_hire_cost) : 0;

  return roundMoney(
    toNumber(row?.paint_cost)
    + toNumber(row?.consumables_cost)
    + optionalFleetCost
    + optionalCarHireCost
    + toNumber(row?.labour_cost)
    + toNumber(row?.parts_cost)
    + toNumber(row?.subcontracted_cost)
    + toNumber(row?.adas_cost)
    + toNumber(row?.storage_recovery_cost),
  );
};

const buildCostShareRows = (row, includedCostColumns) => {
  const categories = [
    { key: 'labour_cost', label: 'Labour' },
    { key: 'parts_cost', label: 'Parts' },
    { key: 'subcontracted_cost', label: 'Subcontracted' },
    { key: 'adas_cost', label: 'ADAS' },
    { key: 'consumables_cost', label: 'Consumables' },
    { key: 'paint_cost', label: 'Paint' },
    ...(includedCostColumns.fleet_cost ? [{ key: 'fleet_cost', label: 'Fleet' }] : []),
    ...(includedCostColumns.car_hire_cost ? [{ key: 'car_hire_cost', label: 'Car Hire' }] : []),
    { key: 'storage_recovery_cost', label: 'Storage & Recovery' },
  ];
  const totalCost = calculateSelectedTotalCost(row, includedCostColumns);

  return categories.map((category) => {
    const cost = toNumber(row?.[category.key]);
    return {
      ...category,
      cost,
      color: COST_CATEGORY_COLORS[category.key],
      percent: totalCost ? roundMoney((cost / totalCost) * 100) : 0,
    };
  }).sort((left, right) => {
    const percentDelta = toNumber(right.percent) - toNumber(left.percent);
    if (percentDelta !== 0) return percentDelta;

    const costDelta = toNumber(right.cost) - toNumber(left.cost);
    if (costDelta !== 0) return costDelta;

    return left.label.localeCompare(right.label);
  });
};

const buildDonutGradient = (costShareRows) => {
  const positiveRows = costShareRows.filter((row) => row.cost > 0);
  const total = positiveRows.reduce((sum, row) => sum + row.cost, 0);
  if (!total) return '#e5e7eb';

  let cursor = 0;
  const stops = positiveRows.map((row) => {
    const start = cursor;
    cursor += (row.cost / total) * 360;
    return `${row.color} ${start}deg ${cursor}deg`;
  });

  return `conic-gradient(${stops.join(', ')})`;
};

const defaultTotals = {
  sales: 0,
  paint_cost: 0,
  consumables_cost: 0,
  fleet_cost: 0,
  car_hire_cost: 0,
  paint_and_consumables_cost: 0,
  labour_cost: 0,
  parts_cost: 0,
  subcontracted_cost: 0,
  adas_cost: 0,
  storage_recovery_cost: 0,
  total_cost: 0,
  gross_profit: 0,
  gross_profit_percent: 0,
};

const PROFIT_VIEW_MODES = {
  DOCUMENT_DATE: 'document_date',
  ACTUAL_SALES: 'actual_sales',
};

const ProfitBreakdownTab = ({ baseFilters, panelWidth }) => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(defaultTotals);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [expandedSiteKey, setExpandedSiteKey] = useState('');
  const [expandedChartSiteKey, setExpandedChartSiteKey] = useState('');
  const [labourBreakdownLevel, setLabourBreakdownLevel] = useState('role');
  const [profitViewMode, setProfitViewMode] = useState(PROFIT_VIEW_MODES.DOCUMENT_DATE);
  const [includedCostColumns, setIncludedCostColumns] = useState({
    fleet_cost: false,
    car_hire_cost: false,
  });
  const [labourDetailSort, setLabourDetailSort] = useState({ key: 'labour_cost', direction: 'desc' });
  const [supplierDetailSorts, setSupplierDetailSorts] = useState({});


  const panelPaperSx = useMemo(
    () => (panelWidth
      ? { width: panelWidth, maxWidth: panelWidth, ml: 'auto', mr: 0, boxSizing: 'border-box' }
      : { width: '100%' }),
    [panelWidth],
  );

  useEffect(() => {
    let isMounted = true;
    const fetchBreakdown = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {
          region_id: filterParamValue(baseFilters?.regionId),
          location_id: filterParamValue(baseFilters?.locationId),
          supplier_id: filterParamValue(baseFilters?.supplierId),
          start_date: baseFilters?.startDate || undefined,
          end_date: baseFilters?.endDate || undefined,
          cost_by_address: baseFilters?.costBy || undefined,
          nominal_code_id: filterParamValue(baseFilters?.nominalCodeId),
          profit_view_mode: profitViewMode,
        };
        Object.keys(params).forEach((key) => {
          if (params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await axiosInstance.get('/api/v1/profit-breakdown', { params });
        if (!isMounted) return;
        const data = response?.data?.data || {};
        setRows(Array.isArray(data.rows) ? data.rows : []);
        setTotals({ ...defaultTotals, ...(data.totals || {}) });
        setExpandedSiteKey('');
        setExpandedChartSiteKey('');
      } catch (fetchError) {
        if (!isMounted) return;
        setError(fetchError?.response?.data?.error || fetchError?.response?.data?.message || 'Failed to load profit breakdown.');
      } finally {
        if (isMounted) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    };

    fetchBreakdown();
    return () => {
      isMounted = false;
    };
  }, [baseFilters, profitViewMode]);

  const showInitialSpinner = loading && !hasLoadedOnce;
  const showOverlay = loading && hasLoadedOnce;

  const getRowKey = (row) => row?.location_id || row?.site_name || row?.location_name || 'unassigned';

  const supplierCostSections = [
    { key: 'parts', title: 'Parts by Supplier' },
    { key: 'subcontracted', title: 'Subcontracted by Supplier' },
    { key: 'adas', title: 'ADAS by Supplier' },
    { key: 'storage_recovery', title: 'Storage & Recovery by Supplier' },
  ];

  const stickyExpandedRowSx = {
    position: 'sticky',
    top: 40,
    zIndex: 2,
  };

  const optionalCostColumns = useMemo(
    () => [
      { key: 'fleet_cost', label: 'Fleet', width: '7%' },
      { key: 'car_hire_cost', label: 'Car Hire', width: '7%' },
    ],
    [],
  );

  const visibleOptionalCostColumns = useMemo(
    () => optionalCostColumns.filter((column) => includedCostColumns[column.key]),
    [includedCostColumns, optionalCostColumns],
  );

  const profitTableColumnCount = 12 + visibleOptionalCostColumns.length;
  const profitTableMinWidth = 1850 + (visibleOptionalCostColumns.length * 100);

  const toggleOptionalCostColumn = (columnKey) => {
    setIncludedCostColumns((current) => ({
      ...current,
      [columnKey]: !current[columnKey],
    }));
  };

  const nextSortDirection = (currentSort, key, defaultDirection = 'asc') => {
    if (currentSort?.key === key) {
      return currentSort.direction === 'asc' ? 'desc' : 'asc';
    }
    return defaultDirection;
  };

  const handleLabourDetailSort = (key, defaultDirection = 'asc') => {
    setLabourDetailSort((current) => ({
      key,
      direction: nextSortDirection(current, key, defaultDirection),
    }));
  };

  const handleSupplierDetailSort = (sectionKey, key, defaultDirection = 'asc') => {
    setSupplierDetailSorts((current) => {
      const currentSectionSort = current[sectionKey];
      return {
        ...current,
        [sectionKey]: {
          key,
          direction: nextSortDirection(currentSectionSort, key, defaultDirection),
        },
      };
    });
  };

  const renderSortableCell = (label, key, sortConfig, onSort, { align = 'left', defaultDirection = 'asc' } = {}) => (
    <TableCell
      sx={{
        fontWeight: 600,
        bgcolor: '#eef2f7',
        whiteSpace: 'nowrap',
        '&.MuiTableCell-stickyHeader': {
          top: 72,
          zIndex: 3,
          bgcolor: '#eef2f7',
        },
      }}
      align={align}
      sortDirection={sortConfig?.key === key ? sortConfig.direction : false}
    >
      <TableSortLabel
        active={sortConfig?.key === key}
        direction={sortConfig?.key === key ? sortConfig.direction : defaultDirection}
        onClick={() => onSort(key, defaultDirection)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const displayRows = useMemo(
    () => rows.map((row) => {
      const totalCost = calculateSelectedTotalCost(row, includedCostColumns);
      const sales = toNumber(row?.sales);
      const grossProfit = roundMoney(sales - totalCost);
      const grossProfitPercent = sales ? roundMoney((grossProfit / sales) * 100) : 0;

      return {
        ...row,
        total_cost: totalCost,
        gross_profit: grossProfit,
        gross_profit_percent: grossProfitPercent,
      };
    }),
    [includedCostColumns, rows],
  );

  const displayTotals = useMemo(() => {
    const totalCost = calculateSelectedTotalCost(totals, includedCostColumns);
    const sales = toNumber(totals?.sales);
    const grossProfit = roundMoney(sales - totalCost);
    const grossProfitPercent = sales ? roundMoney((grossProfit / sales) * 100) : 0;

    return {
      ...totals,
      total_cost: totalCost,
      gross_profit: grossProfit,
      gross_profit_percent: grossProfitPercent,
    };
  }, [includedCostColumns, totals]);

  const sortedRows = useMemo(
    () => [...displayRows].sort((left, right) => {
      const percentDelta = toNumber(right?.gross_profit_percent) - toNumber(left?.gross_profit_percent);
      if (percentDelta !== 0) return percentDelta;

      const profitDelta = toNumber(right?.gross_profit) - toNumber(left?.gross_profit);
      if (profitDelta !== 0) return profitDelta;

      return String(left?.location_name || left?.site_name || '').localeCompare(String(right?.location_name || right?.site_name || ''));
    }),
    [displayRows],
  );
  const grossProfitPercentRows = sortedRows;
  const grossProfitPercentMagnitude = useMemo(
    () => Math.max(1, ...grossProfitPercentRows.map((row) => Math.abs(toNumber(row?.gross_profit_percent)))),
    [grossProfitPercentRows],
  );

  return (
    <Box sx={{ mt: 3, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {showInitialSpinner ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, ...panelPaperSx }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 2, ...panelPaperSx }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: { xs: 2, md: 3 }, ...panelPaperSx, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {showOverlay && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.65)',
                zIndex: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress size={30} />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Profit Breakdown
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  bgcolor: '#f8fafc',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0 }}>
                  View
                </Typography>
                <RadioGroup
                  row
                  value={profitViewMode}
                  onChange={(event) => setProfitViewMode(event.target.value)}
                  sx={{ gap: 0.5 }}
                >
                  <FormControlLabel
                    value={PROFIT_VIEW_MODES.DOCUMENT_DATE}
                    label="Document Date View"
                    control={<Radio size="small" />}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', fontWeight: 600 } }}
                  />
                  <FormControlLabel
                    value={PROFIT_VIEW_MODES.ACTUAL_SALES}
                    label="Actual Sales View"
                    control={<Radio size="small" />}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', fontWeight: 600 } }}
                  />
                </RadioGroup>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  bgcolor: '#f8fafc',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0 }}>
                  Optional costs
                </Typography>
                {optionalCostColumns.map((column) => (
                  <FormControlLabel
                    key={column.key}
                    label={column.label}
                    control={(
                      <Checkbox
                        size="small"
                        checked={includedCostColumns[column.key]}
                        onChange={() => toggleOptionalCostColumn(column.key)}
                      />
                    )}
                    sx={{
                      m: 0,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: includedCostColumns[column.key] ? '#e8f1ff' : '#ffffff',
                      border: '1px solid',
                      borderColor: includedCostColumns[column.key] ? '#90caf9' : 'divider',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Paper sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', order: 2 }}>
            <TableContainer sx={{ maxHeight: '40vh', overflowX: 'auto', overflowY: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: profitTableMinWidth, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: visibleOptionalCostColumns.length ? '14%' : '16%', bgcolor: '#eef2f7' }}>Site</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '8%', bgcolor: '#eef2f7' }} align="right">Sales</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '9%', bgcolor: '#eef2f7' }} align="right">Total Cost</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '7%', bgcolor: '#eef2f7' }} align="right">Paint</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '8%', bgcolor: '#eef2f7' }} align="right">Consumables</TableCell>
                    {visibleOptionalCostColumns.map((column) => (
                      <TableCell key={column.key} sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: column.width, bgcolor: '#eef2f7' }} align="right">{column.label}</TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '7%', bgcolor: '#eef2f7' }} align="right">Labour</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '7%', bgcolor: '#eef2f7' }} align="right">Parts</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '10%', bgcolor: '#eef2f7' }} align="right">Subcontracted</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '7%', bgcolor: '#eef2f7' }} align="right">ADAS</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '10%', bgcolor: '#eef2f7' }} align="right">Storage &amp; Recovery</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '9%', bgcolor: '#eef2f7' }} align="right">Gross Profit</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: '6%', bgcolor: '#eef2f7' }} align="right">Gross Profit %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.length ? sortedRows.map((row, index) => {
                    const rowKey = getRowKey(row);
                    const isExpanded = expandedSiteKey === rowKey;
                    const labourDetails = labourBreakdownLevel === 'technician'
                      ? (Array.isArray(row.labour_technician_details) ? row.labour_technician_details : Array.isArray(row.labour_details) ? row.labour_details : [])
                      : (Array.isArray(row.labour_role_details) ? row.labour_role_details : []);
                    const sortedLabourDetails = sortByConfig(labourDetails, labourDetailSort, {
                      technician: { type: 'text', value: (detail) => detail.full_name || '' },
                      role: { type: 'text', value: (detail) => detail.job_title || detail.normalized_job_title || '' },
                      hours: { type: 'number', value: (detail) => detail.labour_hours },
                      labour_cost: { type: 'number', value: (detail) => detail.labour_cost },
                    });
                    const supplierBreakdowns = row.supplier_breakdowns || {};
                    return [
                      <TableRow key={rowKey} sx={{ bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent',
                          backgroundColor: isExpanded ? '#dbeafe !important' : index % 2 ? '#fcfcfd !important' : 'transparent',
                          ...(isExpanded ? stickyExpandedRowSx : {}),
                        }}
                      >
                        <ButtonBase
                          onClick={() => setExpandedSiteKey(isExpanded ? '' : rowKey)}
                          sx={{ display: 'flex', width: '100%', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5, textAlign: 'left', pl: 0.5 }}
                        >
                          <ChevronRight sx={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s ease' }} />
                          <Box component="span">{row.location_name || row.site_name || 'Unassigned'}</Box>
                        </ButtonBase>
                      </TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.sales)}</TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.total_cost)}</TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.paint_cost)}</TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.consumables_cost)}</TableCell>
                      {visibleOptionalCostColumns.map((column) => (
                        <TableCell key={`${rowKey}-${column.key}`} align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row[column.key])}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.labour_cost)}</TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.parts_cost)}</TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.subcontracted_cost)}</TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.adas_cost)}</TableCell>
                      <TableCell align="right" sx={{ ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.storage_recovery_cost)}</TableCell>
                      <TableCell align="right" sx={{ color: getProfitColor(row.gross_profit), ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatCurrency(row.gross_profit)}</TableCell>
                      <TableCell align="right" sx={{ color: getProfitColor(row.gross_profit_percent), ...(isExpanded ? stickyExpandedRowSx : {}), bgcolor: isExpanded ? '#dbeafe' : index % 2 ? '#fcfcfd' : 'transparent' }}>{formatPercent(row.gross_profit_percent)}</TableCell>
                    </TableRow>,
                    <TableRow key={`${rowKey}-details`}>
                      <TableCell colSpan={profitTableColumnCount} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              px: 2,
                              py: 2,
                              bgcolor: '#f8fafc',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              mb: 1,
                              overflow: 'visible',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, mb: 1, bgcolor: '#f8fafc', py: 0.5, flexDirection: { xs: 'column', md: 'row' } }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Labour breakdown
                              </Typography>
                              <RadioGroup
                                row
                                value={labourBreakdownLevel}
                                onChange={(event) => setLabourBreakdownLevel(event.target.value)}
                              >
                                <FormControlLabel value="role" control={<Radio size="small" />} label="Role" />
                                <FormControlLabel value="technician" control={<Radio size="small" />} label="Technician" />
                              </RadioGroup>
                            </Box>
                            {labourDetails.length ? (
                              <TableContainer sx={{ overflow: 'visible' }}>
                                <Table size="small" stickyHeader sx={{ minWidth: 650 }}>
                                <TableHead>
                                  <TableRow>
                                    {labourBreakdownLevel === 'technician' && (
                                      renderSortableCell('Technician', 'technician', labourDetailSort, handleLabourDetailSort)
                                    )}
                                    {renderSortableCell('Role', 'role', labourDetailSort, handleLabourDetailSort)}
                                    {renderSortableCell('Hours', 'hours', labourDetailSort, handleLabourDetailSort, { align: 'right', defaultDirection: 'desc' })}
                                    {renderSortableCell('Labour Cost', 'labour_cost', labourDetailSort, handleLabourDetailSort, { align: 'right', defaultDirection: 'desc' })}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {sortedLabourDetails.map((detail, detailIndex) => (
                                    <TableRow key={`${rowKey}-${labourBreakdownLevel}-${detail.full_name || 'role'}-${detail.normalized_job_title || detail.job_title || detailIndex}`} sx={{ bgcolor: detailIndex % 2 ? '#fcfcfd' : 'transparent' }}>
                                      {labourBreakdownLevel === 'technician' && (
                                        <TableCell>{detail.full_name || '-'}</TableCell>
                                      )}
                                      <TableCell>{detail.job_title || detail.normalized_job_title || '-'}</TableCell>
                                      <TableCell align="right">{Number(detail.labour_hours || 0).toFixed(2)}</TableCell>
                                      <TableCell align="right">{formatCurrency(detail.labour_cost)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No labour records found for this site.
                              </Typography>
                            )}
                          </Box>

                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                            {supplierCostSections.map((section) => {
                              const sectionRows = Array.isArray(supplierBreakdowns?.[section.key]) ? supplierBreakdowns[section.key] : [];
                              const sectionSort = supplierDetailSorts[section.key] || { key: 'supplier_cost', direction: 'desc' };
                              const sortedSectionRows = sortByConfig(sectionRows, sectionSort, {
                                supplier: { type: 'text', value: (detail) => detail.supplier_name || detail.supplier_id || '' },
                                supplier_cost: { type: 'number', value: (detail) => detail.supplier_cost },
                              });
                              return (
                                <Box key={`${rowKey}-${section.key}`} sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, bgcolor: '#ffffff', py: 0.5 }}>
                                    {section.title}
                                  </Typography>
                                  {sectionRows.length ? (
                                    <TableContainer sx={{ overflow: 'visible' }}>
                                      <Table size="small" stickyHeader sx={{ minWidth: 420 }}>
                                      <TableHead>
                                        <TableRow>
                                          {renderSortableCell(section.title, 'supplier', sectionSort, (key, direction) => handleSupplierDetailSort(section.key, key, direction))}
                                          {renderSortableCell('Cost', 'supplier_cost', sectionSort, (key, direction) => handleSupplierDetailSort(section.key, key, direction), { align: 'right', defaultDirection: 'desc' })}
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {sortedSectionRows.map((detail, detailIndex) => (
                                          <TableRow
                                            key={`${rowKey}-${section.key}-${detail.supplier_id || detail.supplier_name || detailIndex}`}
                                            sx={{ bgcolor: detailIndex % 2 ? '#fcfcfd' : 'transparent' }}
                                          >
                                            <TableCell>{detail.supplier_name || '-'}</TableCell>
                                            <TableCell align="right">{formatCurrency(detail.supplier_cost)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                      </Table>
                                    </TableContainer>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No {section.title.toLowerCase()} records found for this site.
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>,
                    ];
                  }) : (
                    <TableRow>
                      <TableCell colSpan={profitTableColumnCount} align="center" sx={{ py: 4 }}>
                        No profit breakdown records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 0, width: '100%', borderTop: '1px solid', borderColor: 'divider' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: profitTableMinWidth }}>
              <TableBody>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, width: visibleOptionalCostColumns.length ? '14%' : '16%' }}>All Sites</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '8%' }}>{formatCurrency(displayTotals.sales)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '9%' }}>{formatCurrency(displayTotals.total_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '7%' }}>{formatCurrency(displayTotals.paint_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '8%' }}>{formatCurrency(displayTotals.consumables_cost)}</TableCell>
                  {visibleOptionalCostColumns.map((column) => (
                    <TableCell key={`totals-${column.key}`} align="right" sx={{ fontWeight: 700, width: column.width }}>{formatCurrency(displayTotals[column.key])}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, width: '7%' }}>{formatCurrency(displayTotals.labour_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '7%' }}>{formatCurrency(displayTotals.parts_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '10%' }}>{formatCurrency(displayTotals.subcontracted_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '7%' }}>{formatCurrency(displayTotals.adas_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '10%' }}>{formatCurrency(displayTotals.storage_recovery_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '9%', color: getProfitColor(displayTotals.gross_profit) }}>{formatCurrency(displayTotals.gross_profit)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: '6%', color: getProfitColor(displayTotals.gross_profit_percent) }}>{formatPercent(displayTotals.gross_profit_percent)}</TableCell>
                </TableRow>
              </TableBody>
              </Table>
            </Box>
          </Paper>

          {grossProfitPercentRows.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#fbfdff', order: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Gross Profit % by Site
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sorted descending by gross profit %. Click a site to view its cost split.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pr: 1 }}>
                {grossProfitPercentRows.map((row) => {
                  const rowKey = getRowKey(row);
                  const grossProfitPercent = toNumber(row?.gross_profit_percent);
                  const grossProfit = toNumber(row?.gross_profit);
                  const isChartExpanded = expandedChartSiteKey === rowKey;
                  const barColor = grossProfitPercent >= 0 ? '#4caf50' : '#d32f2f';
                  const barWidth = `${Math.min(50, (Math.abs(grossProfitPercent) / grossProfitPercentMagnitude) * 50)}%`;
                  const siteName = row?.location_name || row?.site_name || 'Unassigned';
                  const costShareRows = buildCostShareRows(row, includedCostColumns);
                  const donutGradient = buildDonutGradient(costShareRows);

                  return (
                    <Box
                      key={`gross-profit-percent-${rowKey}`}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: isChartExpanded ? '#bfdbfe' : 'transparent',
                        bgcolor: isChartExpanded ? '#eff6ff' : 'transparent',
                      }}
                    >
                      <ButtonBase
                        onClick={() => setExpandedChartSiteKey(isChartExpanded ? '' : rowKey)}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '240px 1fr 160px' },
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                          textAlign: 'left',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <ChevronRight sx={{ fontSize: 18, transform: isChartExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s ease', color: 'text.secondary', flex: '0 0 auto' }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: isChartExpanded ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {siteName}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            position: 'relative',
                            height: 16,
                            borderRadius: 999,
                            bgcolor: '#e5e7eb',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '50%',
                              top: 0,
                              bottom: 0,
                              width: '1px',
                              bgcolor: 'divider',
                              zIndex: 1,
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              ...(grossProfitPercent >= 0
                                ? { left: '50%', width: barWidth }
                                : { right: '50%', width: barWidth }),
                              bgcolor: barColor,
                              borderRadius: grossProfitPercent >= 0 ? '0 999px 999px 0' : '999px 0 0 999px',
                              zIndex: 0,
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: isChartExpanded ? 700 : 500, color: grossProfitPercent >= 0 ? 'success.main' : 'error.main' }}>
                            {formatPercent(grossProfitPercent)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(grossProfit)}
                          </Typography>
                        </Box>
                      </ButtonBase>
                      <Collapse in={isChartExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 1, pb: 1 }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.35fr 1fr' }, gap: 2, p: 2, bgcolor: '#ffffff', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Cost split by category
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {siteName} cost breakdown
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mt: 2 }}>
                                {costShareRows.map((category) => (
                                  <Box key={`${rowKey}-cost-share-${category.key}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 1fr 70px' }, alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                      {category.label}
                                    </Typography>
                                    <Box sx={{ height: 14, borderRadius: 999, bgcolor: '#e5e7eb', overflow: 'hidden' }}>
                                      <Box sx={{ height: '100%', width: `${Math.min(category.percent, 100)}%`, minWidth: category.percent > 0 ? 4 : 0, bgcolor: category.color, borderRadius: 999 }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: category.color, textAlign: 'right' }}>
                                      {formatPercent(category.percent)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, gap: 2, alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 190,
                                  height: 190,
                                  mx: 'auto',
                                  borderRadius: '50%',
                                  background: donutGradient,
                                  display: 'grid',
                                  placeItems: 'center',
                                }}
                              >
                                <Box sx={{ width: 106, height: 106, borderRadius: '50%', bgcolor: '#ffffff', display: 'grid', placeItems: 'center', textAlign: 'center', p: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                                    {siteName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Total cost
                                  </Typography>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    {formatCurrency(row.total_cost)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                                {costShareRows.map((category) => (
                                  <Box key={`${rowKey}-donut-legend-${category.key}`} sx={{ display: 'grid', gridTemplateColumns: '14px 1fr auto', gap: 0.75, alignItems: 'center' }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: category.color }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {category.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: category.color }}>
                                      {formatPercent(category.percent)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ProfitBreakdownTab;
